<template>
  <section class="nf-page explore-page">
    <div class="explore-layout">
      <div class="explore-content-shell">
        <div class="min-w-0">
          <div class="explore-mobile-toolbar mb-2 xl:hidden">
            <NavbarSearchLocation
              :search="searchInput"
              :display-location="displayLocation"
              :suggestions="searchSuggestions"
              :loading="searchLoading"
              root-class="mobile-search-location explore-search-location min-w-0 flex-1"
              @update:search="searchInput = $event"
              @submit-search="submitSearch"
              @open-location="openLocationPicker"
              @select-suggestion="selectSearchSuggestion"
            >
              <template #actions>
                <div class="explore-layout-switch grid h-9 shrink-0 grid-cols-2 rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm" aria-label="Choose feed layout">
                  <button
                    class="grid h-7 w-7 place-items-center rounded-md transition"
                    :class="viewMode === 'post' ? 'bg-blue-50 text-blue-700' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-800'"
                    type="button"
                    aria-label="Instagram post view"
                    :aria-pressed="viewMode === 'post'"
                    @click="setViewMode('post')"
                  >
                    <i class="ri-layout-row-line text-base" aria-hidden="true"></i>
                  </button>
                  <button
                    class="grid h-7 w-7 place-items-center rounded-md transition"
                    :class="viewMode === 'masonry' ? 'bg-blue-50 text-blue-700' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-800'"
                    type="button"
                    aria-label="Masonry grid view"
                    :aria-pressed="viewMode === 'masonry'"
                    @click="setViewMode('masonry')"
                  >
                    <i class="ri-layout-grid-line text-base" aria-hidden="true"></i>
                  </button>
                </div>
              </template>
            </NavbarSearchLocation>
          </div>

          <div v-if="selectedCategoryChip" class="mb-3 flex flex-wrap items-center gap-2">
            <button
              class="inline-flex min-h-9 items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-black shadow-sm"
              :style="selectedCategoryChip.chipStyle"
              type="button"
              @click="clearCategoryFilter"
            >
              <span>{{ selectedCategoryChip.label }}</span>
              <span class="grid h-5 w-5 place-items-center rounded-full" :style="selectedCategoryChip.dismissStyle">
                <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </span>
            </button>
          </div>

          <div v-if="isLoading" class="mt-2">
            <div v-if="viewMode === 'post'" class="mx-auto grid max-w-[35rem] gap-5">
              <FlyerCardSkeleton v-for="index in 4" :key="`explore-post-skeleton-${index}`" :index="index" view="post" />
            </div>
            <div v-else class="flyer-masonry">
              <div v-for="(column, columnIndex) in skeletonFlyerColumns" :key="`explore-skeleton-column-${columnIndex}`" class="flyer-masonry-column">
                <FlyerCardSkeleton v-for="index in column" :key="`explore-grid-skeleton-${index}`" :index="index" view="masonry" />
              </div>
            </div>
          </div>

          <div v-else-if="visibleFlyers.length" class="mt-2">
            <div v-if="viewMode === 'post'" class="mx-auto grid max-w-[35rem] gap-5">
              <template v-for="flyer in visibleFlyers" :key="flyer.id">
                <FlyerCard v-if="isReviewFlyer(flyer)" :flyer="flyer" view="post" />
                <InstagramFlyerCard v-else :flyer="flyer" view="post" />
              </template>
            </div>
            <div v-else class="flyer-masonry">
              <div v-for="(column, columnIndex) in visibleFlyerColumns" :key="`explore-flyer-column-${columnIndex}`" class="flyer-masonry-column">
                <template v-for="flyer in column" :key="flyer.id">
                  <FlyerCard v-if="isReviewFlyer(flyer)" :flyer="flyer" view="masonry" />
                  <InstagramFlyerCard v-else :flyer="flyer" view="masonry" />
                </template>
              </div>
            </div>

            <div ref="loadMoreTarget" class="grid min-h-20 place-items-center">
              <span v-if="isFetchingMore" class="rounded-full bg-white px-4 py-2 text-sm font-black text-slate-500 shadow-sm">
                Loading more flyers...
              </span>
              <span v-else-if="hasMoreFlyers" class="rounded-full bg-white px-4 py-2 text-sm font-black text-slate-500 shadow-sm">
                Scroll to load more
              </span>
              <span v-else class="rounded-full bg-slate-100 px-4 py-2 text-sm font-black text-slate-500">
                You are all caught up.
              </span>
            </div>
          </div>

          <div v-else class="mt-5 rounded-xl border border-slate-200 bg-white p-8 text-center">
            <h2 class="m-0 text-xl font-black text-slate-950">{{ loadError ? 'Unable to load flyers' : 'No flyers found' }}</h2>
            <p class="m-0 mt-2 text-slate-500">
              {{ loadError || 'Try another category to see more local flyer posts.' }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@vueuse/head'
import InstagramFlyerCard from '@/components/common/InstagramFlyerCard.vue'
import FlyerCard from '@/components/common/FlyerCard.vue'
import FlyerCardSkeleton from '@/components/common/FlyerCardSkeleton.vue'
import NavbarSearchLocation from '@/components/common/NavbarSearchLocation.vue'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'
import { formatDayMonthYear } from '@/utils/date'
import { getFlyerBoardId, normalizeFlyerSaved } from '@/utils/flyerDetail'

useHead({
  title: 'Explore Local Flyers | NetFlyr',
  meta: [
    { name: 'description', content: 'Explore local flyers on NetFlyr for nearby events, jobs, rides, housing, services, offers, reviews, and community updates. Search by location, category, and relevance to discover what matters near you.' },
    { name: 'keywords', content: 'local flyers, community flyers, nearby events, local jobs, rideshare posts, housing flyers, service offers, NetFlyr explore' }
  ]
})

const route = useRoute()
const router = useRouter()
const session = useSessionStore()
const activeCategory = ref('all')
const activeSearch = computed(() => (typeof route.query.search === 'string' ? route.query.search.trim() : ''))
const currentSessionUserId = computed(() => (session.isLoggedIn ? session.userId || session.user?.user_id || '' : ''))
const searchInput = ref(activeSearch.value)
const searchSuggestions = ref([])
const searchLoading = ref(false)
const locationRefreshKey = ref(0)
const viewMode = ref('masonry')
const loadMoreTarget = ref(null)
const flyers = ref([])
const contentTypes = ref([])
const contentSections = ref([])
const isLoading = ref(false)
const isFetchingMore = ref(false)
const currentPage = ref(0)
const hasMoreFlyers = ref(true)
const loadError = ref('')
const masonryColumnCount = ref(2)
let observer
let searchTimer
let searchRequestId = 0

const LOCATION_STORAGE_KEY = 'netflyr.location'
const defaultCountryOptions = [
  { location: 'United States', center: { lat: 39.8283, lng: -98.5795 } },
  { location: 'India', center: { lat: 20.5937, lng: 78.9629 } },
  { location: 'Canada', center: { lat: 56.1304, lng: -106.3468 } },
  { location: 'United Kingdom', center: { lat: 55.3781, lng: -3.436 } }
]

const countryTimezones = {
  india: 'Asia/Kolkata',
  canada: 'America/Toronto',
  'united kingdom': 'Europe/London',
  uk: 'Europe/London'
}

const usStateTimezones = {
  al: 'America/Chicago',
  alabama: 'America/Chicago',
  ak: 'America/Anchorage',
  alaska: 'America/Anchorage',
  az: 'America/Phoenix',
  arizona: 'America/Phoenix',
  ar: 'America/Chicago',
  arkansas: 'America/Chicago',
  ca: 'America/Los_Angeles',
  california: 'America/Los_Angeles',
  co: 'America/Denver',
  colorado: 'America/Denver',
  ct: 'America/New_York',
  connecticut: 'America/New_York',
  dc: 'America/New_York',
  de: 'America/New_York',
  delaware: 'America/New_York',
  fl: 'America/New_York',
  florida: 'America/New_York',
  ga: 'America/New_York',
  georgia: 'America/New_York',
  hi: 'Pacific/Honolulu',
  hawaii: 'Pacific/Honolulu',
  ia: 'America/Chicago',
  iowa: 'America/Chicago',
  id: 'America/Boise',
  idaho: 'America/Boise',
  il: 'America/Chicago',
  illinois: 'America/Chicago',
  in: 'America/Indiana/Indianapolis',
  indiana: 'America/Indiana/Indianapolis',
  ks: 'America/Chicago',
  kansas: 'America/Chicago',
  ky: 'America/New_York',
  kentucky: 'America/New_York',
  la: 'America/Chicago',
  louisiana: 'America/Chicago',
  ma: 'America/New_York',
  massachusetts: 'America/New_York',
  md: 'America/New_York',
  maryland: 'America/New_York',
  me: 'America/New_York',
  maine: 'America/New_York',
  mi: 'America/Detroit',
  michigan: 'America/Detroit',
  mn: 'America/Chicago',
  minnesota: 'America/Chicago',
  mo: 'America/Chicago',
  missouri: 'America/Chicago',
  ms: 'America/Chicago',
  mississippi: 'America/Chicago',
  mt: 'America/Denver',
  montana: 'America/Denver',
  nc: 'America/New_York',
  'north carolina': 'America/New_York',
  nd: 'America/Chicago',
  'north dakota': 'America/Chicago',
  ne: 'America/Chicago',
  nebraska: 'America/Chicago',
  nh: 'America/New_York',
  'new hampshire': 'America/New_York',
  nj: 'America/New_York',
  'new jersey': 'America/New_York',
  nm: 'America/Denver',
  'new mexico': 'America/Denver',
  nv: 'America/Los_Angeles',
  nevada: 'America/Los_Angeles',
  ny: 'America/New_York',
  'new york': 'America/New_York',
  oh: 'America/New_York',
  ohio: 'America/New_York',
  ok: 'America/Chicago',
  oklahoma: 'America/Chicago',
  or: 'America/Los_Angeles',
  oregon: 'America/Los_Angeles',
  pa: 'America/New_York',
  pennsylvania: 'America/New_York',
  ri: 'America/New_York',
  'rhode island': 'America/New_York',
  sc: 'America/New_York',
  'south carolina': 'America/New_York',
  sd: 'America/Chicago',
  'south dakota': 'America/Chicago',
  tn: 'America/Chicago',
  tennessee: 'America/Chicago',
  tx: 'America/Chicago',
  texas: 'America/Chicago',
  ut: 'America/Denver',
  utah: 'America/Denver',
  va: 'America/New_York',
  virginia: 'America/New_York',
  vt: 'America/New_York',
  vermont: 'America/New_York',
  wa: 'America/Los_Angeles',
  washington: 'America/Los_Angeles',
  wi: 'America/Chicago',
  wisconsin: 'America/Chicago',
  wv: 'America/New_York',
  'west virginia': 'America/New_York',
  wy: 'America/Denver',
  wyoming: 'America/Denver'
}

const navSections = computed(() =>
  (contentSections.value.length ? contentSections.value : [{ id: 'content-types', label: 'Browse', items: contentTypes.value }])
    .map((section, sectionIndex) => ({
      id: section.id || `section-${sectionIndex}`,
      label: section.label || section.name || 'Browse',
      items: (Array.isArray(section.items) ? section.items : []).map((item) => ({
        label: item.label || item.name || 'Unknown',
        value: String(item.value || item.id || ''),
        colorCode: item.colorCode || item.color_code,
        colorCodeUnselected: item.colorCodeUnselected || item.color_code_unselected,
        chipStyle: buildSelectedChipStyle(item.colorCode || item.color_code),
        dismissStyle: buildSelectedChipDismissStyle(item.colorCode || item.color_code)
      })).filter((item) => item.value)
    }))
    .filter((section) => section.items.length)
)

const mobileNavItems = computed(() => navSections.value.flatMap((section) => section.items))
const selectedCategoryChip = computed(() => {
  if (activeCategory.value === 'all') return null
  return mobileNavItems.value.find((item) => item.value === activeCategory.value) || null
})

const filteredFlyers = computed(() =>
  flyers.value.filter((flyer) => activeCategory.value === 'all' || String(flyer.contentTypeId) === activeCategory.value)
)

const visibleFlyers = computed(() => filteredFlyers.value)
const isReviewFlyer = (flyer = {}) => String(flyer.flyer_type || flyer.flyerType || '').trim().toLowerCase() === 'review'

const distributeByColumn = (items, columnCount) => {
  const count = Math.max(1, Number(columnCount) || 1)
  const columns = Array.from({ length: count }, () => [])
  items.forEach((item, index) => columns[index % count].push(item))
  return columns
}

const visibleFlyerColumns = computed(() => distributeByColumn(visibleFlyers.value, masonryColumnCount.value))
const skeletonFlyerColumns = computed(() => distributeByColumn(Array.from({ length: 10 }, (_, index) => index + 1), masonryColumnCount.value))

const updateMasonryColumnCount = () => {
  if (typeof window === 'undefined') return
  if (window.innerWidth >= 1280) masonryColumnCount.value = 4
  else if (window.innerWidth >= 768) masonryColumnCount.value = 3
  else masonryColumnCount.value = 2
}

const displayLocation = computed(() => {
  locationRefreshKey.value
  const savedLocation = readSavedLocation()
  const parts = String(savedLocation.location || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)

  return parts.length > 2 ? parts.slice(0, 2).join(', ') : savedLocation.location || 'Choose location'
})

const resetFeedState = () => {
  flyers.value = []
  currentPage.value = 0
  hasMoreFlyers.value = true
}

const loadMoreFlyers = () => {
  if (isLoading.value || isFetchingMore.value || !hasMoreFlyers.value) return
  fetchNearbyFlyers({ page: currentPage.value + 1, append: true })
}

const setViewMode = (mode) => {
  viewMode.value = mode === 'post' ? 'post' : 'masonry'
  localStorage.setItem('netflyr.explore-view', viewMode.value)
}

const selectCategory = (category) => {
  const nextQuery = { ...route.query }
  if (!category || category === 'all') {
    delete nextQuery.contentType
  } else {
    nextQuery.contentType = category
  }
  router.replace({ query: nextQuery })
}

const clearCategoryFilter = () => {
  if (activeCategory.value === 'all') return
  selectCategory('all')
}

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '')

const extractFlyers = (payload) => {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.list,
    payload.data,
    payload.result,
    payload.results,
    payload.flyers,
    payload.nearby_flyers,
    payload.data?.list,
    payload.data?.data,
    payload.data?.result,
    payload.data?.flyers
  ]

  return candidates.find(Array.isArray) || []
}

const extractContentTypes = (payload) => {
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.flyer_content_types,
    payload.content_types,
    payload.categories,
    payload.data?.flyer_content_types,
    payload.data?.content_types,
    payload.data?.categories
  ]

  return candidates.find(Array.isArray) || []
}

const extractSections = (payload) => {
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.sections,
    payload.data?.sections,
    payload.list,
    payload.data?.list,
    payload.result,
    payload.data?.result
  ]

  const source = candidates.find((candidate) => Array.isArray(candidate))
  if (!source) return []

  return source.map((section) => ({
    id: section.id || section.section_id || section.name,
    label: section.name || section.section_name || section.label || 'Browse',
    description: section.description || '',
    colorCode: firstValue(section.color_code, section.colorCode, '475569'),
    colorCodeUnselected: firstValue(section.color_code_unselected, section.colorCodeUnselected, 'f8fafc'),
    items: firstValue(
      section.inner_list,
      section.content_types,
      section.types,
      section.items,
      section.children,
      []
    )
  }))
}

const normalizeHexColor = (value, fallback) => {
  const normalized = String(value || fallback).replace('#', '').trim()
  return /^[0-9a-f]{6}$/i.test(normalized) ? normalized : fallback
}

const hexToRgb = (value, fallback = '64748b') => {
  const normalized = normalizeHexColor(value, fallback)
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16)
  }
}

const buildSelectedChipStyle = (color) => {
  const { r, g, b } = hexToRgb(color)
  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.12)`,
    borderColor: `rgba(${r}, ${g}, ${b}, 0.26)`,
    color: `rgb(${r}, ${g}, ${b})`
  }
}

const buildSelectedChipDismissStyle = (color) => {
  const { r, g, b } = hexToRgb(color)
  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.16)`,
    color: `rgb(${r}, ${g}, ${b})`
  }
}

const buildImageUrl = (value) => {
  const image = Array.isArray(value) ? value[0] : value
  if (!image) return ''
  if (/^https?:\/\//i.test(image)) return image
  return `https://test.amplifierapp.xyz/${String(image).replace(/^\/+/, '')}`
}

const inferCurrentCountry = () => {
  const locale = navigator.language || ''
  const region = locale.includes('-') ? locale.split('-').pop() : ''

  if (region && typeof Intl !== 'undefined' && Intl.DisplayNames) {
    const displayNames = new Intl.DisplayNames([locale], { type: 'region' })
    return displayNames.of(region) || 'United States'
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  if (timeZone.includes('Kolkata')) return 'India'
  if (timeZone.includes('London')) return 'United Kingdom'
  if (timeZone.includes('Toronto') || timeZone.includes('Vancouver')) return 'Canada'
  return 'United States'
}

const countryFromLocation = (location) => {
  const normalized = String(location || '').toLowerCase()
  if (normalized.includes('india')) return 'India'
  if (normalized.includes('canada')) return 'Canada'
  if (normalized.includes('united kingdom') || normalized.includes(' uk')) return 'United Kingdom'
  if (normalized.includes('united states') || normalized.includes('usa')) return 'United States'
  return inferCurrentCountry()
}

const normalizeTimezoneText = (value = '') => String(value || '')
  .toLowerCase()
  .replace(/\./g, '')
  .replace(/\s+/g, ' ')
  .trim()

const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

const timezoneFromCountry = (country = '') => {
  const normalized = normalizeTimezoneText(country)
  if (normalized.includes('india')) return 'Asia/Kolkata'
  if (normalized.includes('united states') || normalized.includes('usa')) return ''
  return countryTimezones[normalized] || ''
}

const timezoneFromUsState = (...values) => {
  for (const value of values) {
    const normalized = normalizeTimezoneText(value)
    if (!normalized) continue
    if (usStateTimezones[normalized]) return usStateTimezones[normalized]

    const tokens = normalized.split(/[^a-z]+/).filter(Boolean)
    for (const token of tokens) {
      if (usStateTimezones[token]) return usStateTimezones[token]
    }
  }

  return ''
}

const timezoneFromUsCoords = (coords = null) => {
  const lat = Number(coords?.lat)
  const lng = Number(coords?.lng)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return ''
  if (lat > 50 && lng < -125) return 'America/Anchorage'
  if (lng <= -150) return 'Pacific/Honolulu'
  if (lng <= -114) return 'America/Los_Angeles'
  if (lng <= -101) return 'America/Denver'
  if (lng <= -85) return 'America/Chicago'
  return 'America/New_York'
}

const timezoneFromLocation = (savedLocation = {}) => {
  const text = [
    savedLocation.location,
    savedLocation.country_name,
    savedLocation.state_name,
    savedLocation.city_name,
    savedLocation.address
  ].filter(Boolean).join(', ')
  const normalizedText = normalizeTimezoneText(text)
  const explicitCountry = savedLocation.country_name ||
    (normalizedText.includes('india') ? 'India' : '') ||
    (normalizedText.includes('canada') ? 'Canada' : '') ||
    (normalizedText.includes('united kingdom') || normalizedText.includes(' uk') ? 'United Kingdom' : '')
  const countryTimezone = timezoneFromCountry(explicitCountry)
  if (countryTimezone) return countryTimezone

  const isUnitedStates = normalizeTimezoneText(savedLocation.country_name).includes('united states') ||
    normalizedText.includes('united states') ||
    /\busa\b/.test(normalizedText) ||
    timezoneFromUsState(savedLocation.state_name, savedLocation.location)

  if (isUnitedStates) {
    return timezoneFromUsState(savedLocation.state_name, savedLocation.location, savedLocation.address) ||
      timezoneFromUsCoords(savedLocation.coords) ||
      (browserTimezone().startsWith('America/') ? browserTimezone() : 'America/New_York')
  }

  return timezoneFromCountry(countryFromLocation(text)) || browserTimezone() || 'Asia/Kolkata'
}

const isCountrySelection = (location) => {
  const normalized = String(location || '').trim().toLowerCase()
  return defaultCountryOptions.some((item) => item.location.toLowerCase() === normalized)
}

const readSavedLocation = () => {
  const saved = localStorage.getItem(LOCATION_STORAGE_KEY)
  if (saved) {
    try {
      const parsed = JSON.parse(saved)
      if (parsed?.location) return parsed
    } catch (error) {
      localStorage.removeItem(LOCATION_STORAGE_KEY)
    }
  }

  const country = inferCurrentCountry()
  const option = defaultCountryOptions.find((item) => item.location === country) || defaultCountryOptions[0]
  const fallback = { location: option.location, coords: null, mode: 'country' }
  localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(fallback))
  return fallback
}

const getNearbyFlyerParams = (page = 1) => {
  const savedLocation = readSavedLocation()
  const mode = savedLocation.mode || (isCountrySelection(savedLocation.location) ? 'country' : 'location')
  const isCountry = mode === 'country'
  const country = isCountry ? countryFromLocation(savedLocation.location) : ''
  const coords = !isCountry ? savedLocation.coords : null
  const timezone = session.isLoggedIn
    ? ''
    : timezoneFromLocation({ ...savedLocation, country_name: savedLocation.country_name || country, coords })

  return {
    lat: coords?.lat ? String(coords.lat) : '',
    long: coords?.lng ? String(coords.lng) : '',
    page,
    country,
    timezone,
    id: currentSessionUserId.value,
    content_type: activeCategory.value === 'all' ? '' : activeCategory.value
  }
}

const getSearchFlyerParams = (page = 1) => {
  const { lat, long, country, id } = getNearbyFlyerParams(page)

  return {
    lat,
    long,
    country,
    id,
    search_word: activeSearch.value,
    page
  }
}

const normalizeSearchSuggestion = (item = {}, index = 0) => {
  const category = firstValue(item.category_name, item.cat_name, item.category, item.content_type, 'Flyer')
  const title = firstValue(item.title, item.flyer_title, item.name, item.heading, item.description, category)
  const locationText = firstValue(item.location, item.city, item.state, item.country, item.address, '')
  const image = buildImageUrl(firstValue(item.image, item.flyer_image, item.flyer_img, item.image1, item.photo, item.thumbnail))

  return {
    id: firstValue(item.id, item.flyer_id, item.f_id, `search-${index}`),
    title,
    meta: [category, locationText].filter(Boolean).join(' • '),
    image,
    initial: String(title || category).trim().charAt(0).toUpperCase() || 'F'
  }
}

const fetchSearchSuggestions = async (query = searchInput.value.trim()) => {
  const normalizedQuery = query.trim()
  if (normalizedQuery.length < 2) {
    searchSuggestions.value = []
    searchLoading.value = false
    return
  }

  const requestId = ++searchRequestId
  searchLoading.value = true

  try {
    const { data } = await api.searchFlyers({
      ...getSearchFlyerParams(1),
      search_word: normalizedQuery
    })
    if (requestId !== searchRequestId) return
    searchSuggestions.value = extractFlyers(data).slice(0, 6).map(normalizeSearchSuggestion)
  } catch {
    if (requestId === searchRequestId) {
      searchSuggestions.value = []
    }
  } finally {
    if (requestId === searchRequestId) {
      searchLoading.value = false
    }
  }
}

const submitSearch = () => {
  const query = searchInput.value.trim()
  if (!query) return
  fetchSearchSuggestions(query)
  router.push({ path: '/explore', query: { ...route.query, search: query } })
}

const selectSearchSuggestion = (suggestion) => {
  if (!suggestion?.id) return
  searchSuggestions.value = []
  router.push(`/flyers/${suggestion.id}`)
}

const openLocationPicker = () => {
  window.dispatchEvent(new CustomEvent('netflyr-open-location-picker'))
}

const extractPagination = (payload) => {
  if (!payload || typeof payload !== 'object') return { currentPage: null, totalPages: null }

  const pagination = payload.pagination && typeof payload.pagination === 'object' ? payload.pagination : {}
  const meta = payload.meta && typeof payload.meta === 'object' ? payload.meta : {}

  const current = Number(
    firstValue(
      payload.current_page,
      payload.page,
      pagination.current_page,
      pagination.page,
      meta.current_page,
      meta.page,
      null
    )
  )
  const total = Number(
    firstValue(
      payload.total_pages,
      payload.total_page,
      payload.last_page,
      pagination.total_pages,
      pagination.total_page,
      pagination.last_page,
      meta.total_pages,
      meta.total_page,
      meta.last_page,
      null
    )
  )

  return {
    currentPage: Number.isFinite(current) && current > 0 ? current : null,
    totalPages: Number.isFinite(total) && total > 0 ? total : null
  }
}

const mergeFlyers = (existing, incoming) => {
  const seen = new Set()
  return [...existing, ...incoming].filter((flyer) => {
    const key = String(flyer.id || flyer.flyer_id || '')
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const normalizeFlyer = (flyer, index) => {
  const normalizedFlyerType = String(firstValue(flyer.flyer_type, flyer.flyerType, 'flyer')).trim().toLowerCase()
  const contentTypeId = String(firstValue(flyer.content_type, flyer.contentTypeId, flyer.category, ''))
  const contentType = contentTypes.value.find((type) => String(type.id) === contentTypeId)
  const category = firstValue(contentType?.name, flyer.category_name, flyer.cat_name, flyer.flyer_category, 'Flyer')
  const requestCountry = getNearbyFlyerParams().country
  const address = firstValue(flyer.address, flyer.flyer_address, flyer.location_name, flyer.location, flyer.city, requestCountry)
  const rawDate = firstValue(flyer.date, flyer.created_date, flyer.created_at, flyer.start_date, flyer.updated_at, '')
  const boardId = getFlyerBoardId(flyer)
  const images = [
    ...(Array.isArray(flyer.images) ? flyer.images : []),
    flyer.image,
    flyer.image1,
    flyer.image2,
    flyer.image3,
    flyer.image4
  ].filter(Boolean)

  return {
    ...flyer,
    id: firstValue(flyer.id, flyer.flyer_id, flyer.f_id, index + 1),
    userId: firstValue(
      flyer.user_id,
      flyer.userId,
      flyer.userid,
      flyer.uid,
      flyer.created_by,
      flyer.created_by_id,
      flyer.owner_id,
      flyer.author_id,
      ''
    ),
    title: firstValue(flyer.title, flyer.flyer_title, flyer.name, flyer.heading, category),
    category,
    contentTypeId,
    categoryColor: contentType?.color_code ? `#${normalizeHexColor(contentType.color_code, '64748b')}` : '#64748b',
    location: firstValue(flyer.location, flyer.city, flyer.state, requestCountry),
    address,
    date: formatDayMonthYear(rawDate) || rawDate,
    postedAt: firstValue(flyer.posted_at, flyer.post_date, flyer.created_at, flyer.created_time, flyer.updated_at, flyer.created_date, rawDate),
    boardId,
    board_id: boardId,
    saved: normalizeFlyerSaved(flyer),
    status: firstValue(flyer.status, flyer.flyer_status, ''),
    image: buildImageUrl(firstValue(images, flyer.flyer_image, flyer.flyer_img, flyer.photo, flyer.thumbnail)),
    images: images.map(buildImageUrl),
    param_code: firstValue(flyer.param_code, flyer.paramCode, flyer.code, flyer.color_code, flyer.colorCode, flyer.color, ''),
    code: firstValue(flyer.code, flyer.param_code, flyer.paramCode, flyer.color_code, flyer.colorCode, flyer.color, ''),
    accent: firstValue(flyer.accent, flyer.color, '#8799aa'),
    author: firstValue(flyer.author, flyer.user_name, flyer.name, flyer.username, 'NetFlyr'),
    authorAvatar: buildImageUrl(firstValue(flyer.user_img, flyer.author_img, flyer.profile_image, flyer.avatar)),
    description: firstValue(flyer.description, flyer.flyer_description, flyer.desc, flyer.details, flyer.content, ''),
    hashTags: firstValue(flyer.hash_tags, flyer.hashtags, ''),
    liked: ['1', 'true', 'yes', 'liked'].includes(String(firstValue(flyer.liked, flyer.is_liked, flyer.like_status, flyer.liked_status, '')).trim().toLowerCase()),
    likesCount: Number(firstValue(flyer.likes_count, flyer.likesCount, flyer.like_count, 0)),
    commentsCount: Number(firstValue(
      flyer.comments_count,
      flyer.commentsCount,
      flyer.comment_count,
      normalizedFlyerType === 'review' ? 0 : flyer.review_count,
      0
    )),
    viewsCount: Number(firstValue(flyer.views_count, flyer.viewsCount, 0)),
    shareUrl: firstValue(flyer.share_url, flyer.shareUrl, flyer.link, '')
  }
}

const hasFlyerContent = (flyer) =>
  flyer?.flyer_type === 'flyer' ||
  Boolean(firstValue(flyer?.description, flyer?.image, flyer?.image1, flyer?.share_url, flyer?.user_name))

const fetchContentTypeSections = async () => {
  try {
    const { data } = await api.getFlyerContentTypesSections()
    const sections = extractSections(data)
    const typesFromSections = sections.flatMap((section) => section.items || [])

    contentSections.value = sections
    if (typesFromSections.length) {
      contentTypes.value = typesFromSections.map((item) => ({
        id: firstValue(item.id, item.content_type_id, item.value),
        name: firstValue(item.name, item.label, item.title),
        icon: firstValue(item.icon, item.icon_url, item.image),
        color_code: firstValue(item.color_code, item.colorCode, '64748b'),
        color_code_unselected: firstValue(item.color_code_unselected, item.colorCodeUnselected, 'f1f5f9'),
        description: firstValue(item.description, '')
      }))
    }
  } catch {
    contentSections.value = []
  }
}

const fetchNearbyFlyers = async ({ page = 1, append = false } = {}) => {
  if (append) {
    isFetchingMore.value = true
  } else {
    isLoading.value = true
    loadError.value = ''
  }

  try {
    const requests = [
      activeSearch.value
        ? api.searchFlyers(getSearchFlyerParams(page))
        : api.nearbyFlyers(getNearbyFlyerParams(page))
    ]
    if (!contentSections.value.length) {
      requests.push(fetchContentTypeSections())
    }

    const [{ data: flyerData }] = await Promise.all(requests)
    const extractedFlyers = extractFlyers(flyerData)
      .filter(hasFlyerContent)
      .map(normalizeFlyer)
    const pagination = extractPagination(flyerData)

    if (!contentTypes.value.length) {
      contentTypes.value = extractContentTypes(flyerData)
    }

    flyers.value = append ? mergeFlyers(flyers.value, extractedFlyers) : extractedFlyers
    currentPage.value = extractedFlyers.length ? page : Math.max(currentPage.value, append ? currentPage.value : 1)

    if (pagination.totalPages) {
      hasMoreFlyers.value = (pagination.currentPage || page) < pagination.totalPages
    } else {
      hasMoreFlyers.value = extractedFlyers.length > 0
    }
  } catch (error) {
    if (!append) {
      flyers.value = []
      currentPage.value = 0
      hasMoreFlyers.value = false
      loadError.value = error?.response?.data?.message || error?.message || 'Please try again in a moment.'
    }
  } finally {
    isLoading.value = false
    isFetchingMore.value = false
    await nextTick()
    observeLoadMoreTarget()
  }
}

const handleLocationChange = () => {
  locationRefreshKey.value += 1
  resetFeedState()
  fetchNearbyFlyers({ page: 1 })
}

const syncActiveCategoryFromRoute = () => {
  const nextCategory = typeof route.query.contentType === 'string' && route.query.contentType.trim()
    ? route.query.contentType.trim()
    : 'all'

  if (activeCategory.value === nextCategory && currentPage.value > 0) return
  activeCategory.value = nextCategory
  resetFeedState()
  fetchNearbyFlyers({ page: 1 })
}

const observeLoadMoreTarget = () => {
  if (!observer) return
  observer.disconnect()
  if (!loadMoreTarget.value || !hasMoreFlyers.value) return
  observer.observe(loadMoreTarget.value)
}

onMounted(() => {
  viewMode.value = localStorage.getItem('netflyr.explore-view') === 'post' ? 'post' : 'masonry'
  updateMasonryColumnCount()
  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        loadMoreFlyers()
      }
    },
    { rootMargin: '260px 0px' }
  )

  observeLoadMoreTarget()
  window.addEventListener('resize', updateMasonryColumnCount)
  window.addEventListener('netflyr-location-change', handleLocationChange)
  window.addEventListener('storage', handleLocationChange)
})

watch(() => route.query.contentType, () => {
  syncActiveCategoryFromRoute()
}, { immediate: true })

watch(() => route.query.search, () => {
  searchInput.value = activeSearch.value
  resetFeedState()
  fetchNearbyFlyers({ page: 1 })
})

watch(searchInput, (value) => {
  clearTimeout(searchTimer)
  const query = value.trim()
  if (query.length < 2) {
    searchSuggestions.value = []
    searchLoading.value = false
    return
  }
  searchTimer = setTimeout(() => {
    fetchSearchSuggestions(query)
  }, 250)
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
  clearTimeout(searchTimer)
  window.removeEventListener('resize', updateMasonryColumnCount)
  window.removeEventListener('netflyr-location-change', handleLocationChange)
  window.removeEventListener('storage', handleLocationChange)
})
</script>

<style scoped>
.explore-page {
  padding: 1rem;
  max-width: none;
}

.explore-layout {
  margin-inline: auto;
  width: 100%;
  max-width: none;
}

.explore-content-shell {
  display: grid;
  gap: 1rem;
}

.flyer-masonry {
  display: grid;
  grid-template-columns: repeat(var(--masonry-columns, 2), minmax(0, 1fr));
  gap: 0.5rem;
  align-items: start;
}

.flyer-masonry-column {
  display: grid;
  min-width: 0;
  gap: 0.5rem;
  align-content: start;
}

@media (min-width: 768px) {
  .flyer-masonry {
    --masonry-columns: 3;
    gap: 0.65rem;
  }

  .flyer-masonry-column { gap: 0.65rem; }
}

@media (min-width: 1280px) {
  .flyer-masonry {
    --masonry-columns: 4;
    gap: 0.75rem;
  }

  .flyer-masonry-column { gap: 0.75rem; }
}

@media (max-width: 640px) {
  .explore-page { padding: 0.5rem; }
  .explore-content-shell { gap: 0.5rem; }
}

@media (min-width: 1024px) {
  .explore-page {
    padding: 1rem 1rem 2rem;
  }
}
</style>
